import { useCreateOrUpdateSculptureEditionInAuction } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import { FormLayoutElement } from "@/config/forms/general";
import { SculptureEditionInAuction } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const SculptureEditionInAuctionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: SculptureEditionInAuction;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const defaultFields = useMemo(
    (): FormLayoutElement[] => [
      {
        name: "sculptureEditionDansVentesNumeroLot",
        type: "text",
        label: t("sculptureEditionDansVentesNumeroLot"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesReproduitPage",
        type: "text",
        label: t("sculptureEditionDansVentesReproduitPage"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesEstimationBasse",
        type: "inputSelectCurrency",
        label: t("sculptureEditionDansVentesEstimationBasse"),
        className: "w-full",
        defaultSelectValue: "EUR",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesEstimationHaute",
        type: "inputSelectCurrency",
        label: t("sculptureEditionDansVentesEstimationHaute"),
        className: "w-full",
        defaultSelectValue: "EUR",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesVendu",
        type: "checkbox",
        label: t("sculptureEditionDansVentesVendu"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesResultatNonListe",
        type: "checkbox",
        label: t("sculptureEditionDansVentesResultatNonListe"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesNonVenu",
        type: "checkbox",
        label: t("sculptureEditionDansVentesNonVenu"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesNonVendu",
        type: "checkbox",
        label: t("sculptureEditionDansVentesNonVendu"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesNonReproduit",
        type: "checkbox",
        label: t("sculptureEditionDansVentesNonReproduit"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesRetire",
        type: "checkbox",
        label: t("sculptureEditionDansVentesRetire"),
        className: "w-full",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesMiseAPrix",
        type: "inputSelectCurrency",
        label: t("sculptureEditionDansVentesMiseAPrix"),
        className: "w-full",
        defaultSelectValue: "EUR",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesPrixMarteau",
        type: "inputSelectCurrency",
        label: t("sculptureEditionDansVentesPrixMarteau"),
        className: "w-full",
        defaultSelectValue: "EUR",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesPrixAvecFrais",
        type: "inputSelectCurrency",
        label: t("sculptureEditionDansVentesPrixAvecFrais"),
        className: "w-full",
        defaultSelectValue: "EUR",
      } as FormLayoutElement,
      {
        name: "sculptureEditionDansVentesNotes",
        type: "richtext",
        label: t("sculptureEditionDansVentesNotes"),
        className: "w-full",
      } as FormLayoutElement,
    ],
    [t],
  );

  const schema = z.object({
    sculptureEditionDansVentesNumeroLot: z.string().optional(),
    sculptureEditionDansVentesReproduitPage: z.string().optional(),
    sculptureEditionDansVentesEstimationHaute: z.string().optional(),
    sculptureEditionDansVentesEstimationBasse: z.string().optional(),
    sculptureEditionDansVentesVendu: z.boolean().optional(),
    sculptureEditionDansVentesResultatNonListe: z.boolean().optional(),
    sculptureEditionDansVentesNonVenu: z.boolean().optional(),
    sculptureEditionDansVentesNonVendu: z.boolean().optional(),
    sculptureEditionDansVentesNonReproduit: z.boolean().optional(),
    sculptureEditionDansVentesRetire: z.boolean().optional(),
    sculptureEditionDansVentesMiseAPrix: z.string().optional(),
    sculptureEditionDansVentesPrixMarteau: z.string().optional(),
    sculptureEditionDansVentesPrixAvecFrais: z.string().optional(),
    sculptureEditionDansVentesNotes: z.string().optional(),
    auction: z
      .object({
        ventesDescriptionNumeroVente: z.string().optional(),
        ventesDescriptionResume: z.string().optional(),
      })
      .optional(),
    sculptureEdition: z
      .object({
        sculptureEditionNumeroEnregistrement: z.string().optional(),
        sculptureEditionNotes: z.string().optional(),
      })
      .optional(),
  });

  const { mutate: createOrUpdate } =
    useCreateOrUpdateSculptureEditionInAuction(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      auction: data.auction,
      sculptureEdition: data.sculptureEdition,
      sculptureEditionDansVentesNumeroLot:
        data.sculptureEditionDansVentesNumeroLot ?? "",
      sculptureEditionDansVentesReproduitPage:
        data.sculptureEditionDansVentesReproduitPage ?? "",
      sculptureEditionDansVentesEstimationHaute:
        data.sculptureEditionDansVentesEstimationHaute ?? "",
      sculptureEditionDansVentesEstimationBasse:
        data.sculptureEditionDansVentesEstimationBasse ?? "",
      sculptureEditionDansVentesVendu: data.sculptureEditionDansVentesVendu,
      sculptureEditionDansVentesResultatNonListe:
        data.sculptureEditionDansVentesResultatNonListe,
      sculptureEditionDansVentesNonVenu: data.sculptureEditionDansVentesNonVenu,
      sculptureEditionDansVentesNonVendu:
        data.sculptureEditionDansVentesNonVendu,
      sculptureEditionDansVentesNonReproduit:
        data.sculptureEditionDansVentesNonReproduit,
      sculptureEditionDansVentesRetire: data.sculptureEditionDansVentesRetire,
      sculptureEditionDansVentesMiseAPrix:
        data.sculptureEditionDansVentesMiseAPrix ?? "",
      sculptureEditionDansVentesPrixMarteau:
        data.sculptureEditionDansVentesPrixMarteau ?? "",
      sculptureEditionDansVentesPrixAvecFrais:
        data.sculptureEditionDansVentesPrixAvecFrais ?? "",
      sculptureEditionDansVentesNotes:
        data.sculptureEditionDansVentesNotes ?? "",
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdate({
      id: data.id,
      sculptureEditionId: data.sculptureEdition.id,
      auctionId: data.auction.id,
      sculptureEditionDansVentesNumeroLot:
        formData.sculptureEditionDansVentesNumeroLot,
      sculptureEditionDansVentesReproduitPage:
        formData.sculptureEditionDansVentesReproduitPage,
      sculptureEditionDansVentesEstimationHaute:
        formData.sculptureEditionDansVentesEstimationHaute,
      sculptureEditionDansVentesEstimationBasse:
        formData.sculptureEditionDansVentesEstimationBasse,
      sculptureEditionDansVentesVendu: formData.sculptureEditionDansVentesVendu,
      sculptureEditionDansVentesResultatNonListe:
        formData.sculptureEditionDansVentesResultatNonListe,
      sculptureEditionDansVentesNonVenu:
        formData.sculptureEditionDansVentesNonVenu,
      sculptureEditionDansVentesNonVendu:
        formData.sculptureEditionDansVentesNonVendu,
      sculptureEditionDansVentesNonReproduit:
        formData.sculptureEditionDansVentesNonReproduit,
      sculptureEditionDansVentesRetire:
        formData.sculptureEditionDansVentesRetire,
      sculptureEditionDansVentesMiseAPrix:
        formData.sculptureEditionDansVentesMiseAPrix,
      sculptureEditionDansVentesPrixMarteau:
        formData.sculptureEditionDansVentesPrixMarteau,
      sculptureEditionDansVentesPrixAvecFrais:
        formData.sculptureEditionDansVentesPrixAvecFrais,
      sculptureEditionDansVentesNotes: formData.sculptureEditionDansVentesNotes,
    });
  };

  const isFromAuction = type === "auction";
  const previewConfig = isFromAuction
    ? {
        attachments: data.sculptureEdition?.attachments ?? [],
        type: "sculptureEdition" as const,
        alt:
          data.sculptureEdition?.sculptureEditionNumeroEnregistrement ??
          data.sculptureEdition?.sculptureEditionNumeroInventaire,
      }
    : {
        attachments: data.auction?.attachments ?? [],
        type: "auction" as const,
        alt:
          data.auction?.ventesDescriptionNumeroVente ??
          data.auction?.ventesDescriptionTitre,
      };

  const layoutFields =
    fields && fields.length > 0 ? fields : defaultFields;

  return (
    <LinksDrawerLayout
      form={form}
      fields={layoutFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default SculptureEditionInAuctionDrawer;
